import { useEffect, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import Page from '@/components/Page'
import BackButton from '@/components/BackButton'
import { Button } from '@/components/chadcn/Button'
import { Label } from '@/components/chadcn/Label'
import Spinner from '@/components/Spinner'
import { SelectInput } from '@/components/InputFields'
import DatePicker from '@/components/DatePicker'
import HeightPicker from '@/screens/onboarding/components/HeightPicker'
import {
  patientProfileSchema,
  type PatientProfileFormData,
} from '@/schemas/patient'
import { useSelfQuery, useUpdatePatientProfileMutation } from '@/data/queries'
import { kgToLbs, lbsToKg } from '@/util/units'

const numberOfFetusesOptions = [1, 2, 3, 4].map((n) => ({
  value: String(n) as '1' | '2' | '3' | '4',
  title: String(n),
}))

const round2 = (value: number) => Math.round(value * 100) / 100

const UpdateProfilePage = () => {
  const { data: self, isPending: isLoadingSelf } = useSelfQuery()
  const { mutateAsync: updateProfile, isPending: isUpdating } =
    useUpdatePatientProfileMutation()

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<PatientProfileFormData>({
    mode: 'all',
    resolver: zodResolver(patientProfileSchema),
  })

  // Reset from server only on first arrival of `self`. useSelfQuery's
  // `select` returns a new reference per render, so without the ref guard
  // the form would reset on every refetch and clobber pending edits.
  const initialisedRef = useRef(false)
  useEffect(() => {
    if (self && !initialisedRef.current) {
      initialisedRef.current = true
      reset({
        heightInCentimeters: self.heightInCentimeters ?? undefined,
        // UI shows lbs but the schema/API field is kg — store the kg value
        // pre-rounded to lbs precision so the displayed lbs value parses
        // back to the same kg on submit.
        weightInKilograms: self.weightInKilograms
          ? lbsToKg(round2(kgToLbs(self.weightInKilograms)))
          : undefined,
        numberOfFetuses: self.numberOfFetuses ?? 1,
        estimatedDateOfDelivery: self.estimatedDateOfDelivery?.toString() ?? '',
      })
    }
  }, [self, reset])

  const isPending = isLoadingSelf || isUpdating

  const onSubmit = async (formData: PatientProfileFormData) => {
    const patientId = self?.id ?? ''
    try {
      await updateProfile({
        patientId,
        profile: {
          heightInCentimeters: formData.heightInCentimeters,
          weightInKilograms: formData.weightInKilograms,
          numberOfFetuses: formData.numberOfFetuses,
          estimatedDateOfDelivery: formData.estimatedDateOfDelivery,
        },
      })
      toast.success('Profile saved.')
    } catch {
      toast.error('Failed to save profile. Please try again.')
    }
  }

  return (
    <Page showAppbar={false}>
      <div className="flex min-h-[calc(100vh-56px)] flex-col justify-between">
        <div className="flex flex-col gap-6">
          <BackButton />

          <h1 className="text-[26px] font-medium text-primary-contrast">
            Update your profile
          </h1>

          <form
            id="update-profile-form"
            className="flex flex-col gap-5"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-1">
              <Label htmlFor="weightInLbs">
                Current weight?{' '}
                <span className="text-sm text-primary-contrast/60">(lbs)</span>
              </Label>
              <Controller
                name="weightInKilograms"
                control={control}
                render={({ field }) => (
                  <input
                    id="weightInLbs"
                    type="number"
                    step="any"
                    disabled={isPending}
                    className="block w-full rounded-lg border-0 px-3 py-2 text-base ring-1 ring-inset ring-input focus:ring-2 focus:ring-primary disabled:text-primary-contrast/60"
                    value={field.value ? round2(kgToLbs(field.value)) : ''}
                    onChange={(event) => {
                      const value = Number(event.target.value)
                      if (!Number.isNaN(value) && value > 0) {
                        field.onChange(lbsToKg(value))
                      } else {
                        field.onChange(undefined)
                      }
                    }}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.weightInKilograms && (
                <p className="text-sm text-error">
                  {errors.weightInKilograms.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <Label>
                Height?{' '}
                <span className="text-sm text-primary-contrast/60">
                  (Feet and inches)
                </span>
              </Label>
              <Controller
                name="heightInCentimeters"
                control={control}
                render={({ field }) => (
                  <HeightPicker
                    valueCm={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.heightInCentimeters && (
                <p className="text-sm text-error">
                  {errors.heightInCentimeters.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="estimatedDateOfDelivery">
                Estimated due date?
              </Label>
              <Controller
                name="estimatedDateOfDelivery"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    id="estimatedDateOfDelivery"
                    disabled={isPending}
                    isError={!!errors.estimatedDateOfDelivery}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.estimatedDateOfDelivery && (
                <p className="text-sm text-error">
                  {errors.estimatedDateOfDelivery.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="numberOfFetuses">
                How many babies are you having?
              </Label>
              <Controller
                name="numberOfFetuses"
                control={control}
                render={({ field }) => (
                  <SelectInput
                    id="numberOfFetuses"
                    disabled={isPending}
                    value={String(field.value)}
                    options={numberOfFetusesOptions}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value, 10))
                    }
                  />
                )}
              />
              {errors.numberOfFetuses && (
                <p className="text-sm text-error">
                  {errors.numberOfFetuses.message}
                </p>
              )}
            </div>
          </form>
        </div>

        <Button
          type="submit"
          form="update-profile-form"
          disabled={isPending}
          className="flex w-full gap-3 py-2 text-base"
        >
          {isPending && <Spinner />}
          Update
        </Button>
      </div>
    </Page>
  )
}

export default UpdateProfilePage
